import type { GuaEvent } from "@/mocks/events";
import StatusTag from "./StatusTag";

interface EventRowProps {
  event: GuaEvent;
  index: number;
  open: boolean;
  onToggle: () => void;
}

export default function EventRow({ event, index, open, onToggle }: EventRowProps) {
  const top = index < 3;
  return (
    <article className="overflow-hidden rounded-2xl border border-background-200 bg-background-50 transition-shadow hover:border-background-300">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={`timeline-${event.id}`}
        className="flex w-full items-center gap-3 p-4 text-left md:gap-4 md:px-5"
      >
        <span
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg font-heading text-base font-bold md:h-10 md:w-10 ${
            top ? "bg-primary-500 text-background-50" : "bg-background-200 text-foreground-600"
          }`}
        >
          {index + 1}
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <h3 className="font-heading text-sm font-bold text-foreground-900 md:text-base">
              {event.title}
            </h3>
            <span className="hidden rounded-full bg-background-100 px-2 py-0.5 text-[11px] font-medium text-foreground-500 md:inline-flex">
              {event.category}
            </span>
            <span className="inline-flex items-center gap-1 whitespace-nowrap text-[11px] font-medium text-foreground-500">
              <i className="ri-time-line" />
              {event.updated}
            </span>
          </div>
          <p className="mt-1 hidden truncate text-xs text-foreground-600 sm:block">{event.summary}</p>
        </div>

        <div className="hidden shrink-0 flex-col items-end gap-1.5 md:flex">
          <div className="flex items-center gap-1 text-xs font-semibold text-primary-600">
            <i className="ri-fire-fill" />
            {event.heat}
          </div>
          <StatusTag status={event.status} />
        </div>

        <span
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-background-200 text-foreground-600 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        >
          <i className="ri-arrow-down-s-line text-lg" />
        </span>
      </button>

      {open ? (
        <div id={`timeline-${event.id}`} className="border-t border-background-200 bg-background-100/50 px-4 py-5 md:px-6">
          <div className="flex flex-wrap items-center gap-2 md:hidden">
            <StatusTag status={event.status} />
          </div>

          <p className="mt-3 text-sm leading-relaxed text-foreground-700 md:mt-0">{event.summary}</p>

          <div className="mt-5 grid gap-6 lg:grid-cols-[1fr_1.3fr_1fr]">
            {/* cause */}
            <div className="rounded-xl border border-background-200 bg-background-50 p-4">
              <div className="flex items-center gap-2 text-sm font-bold text-foreground-900">
                <i className="ri-seedling-line text-primary-600" />
                起因
              </div>
              <p className="mt-2 text-[13px] leading-relaxed text-foreground-600">{event.cause}</p>
            </div>

            {/* steps */}
            <div className="rounded-xl border border-background-200 bg-background-50 p-4">
              <div className="flex items-center gap-2 text-sm font-bold text-foreground-900">
                <i className="ri-node-tree text-accent-700" />
                时间线经过
              </div>
              <ol className="mt-3 space-y-3">
                {event.steps.map((step) => (
                  <li key={step.date + step.title} className="relative flex gap-3">
                    <span className="mt-1 flex flex-col items-center">
                      <span className="h-2 w-2 rounded-full bg-accent-500 ring-4 ring-accent-100" />
                      <span className="mt-1 w-px flex-1 bg-background-300" />
                    </span>
                    <div className="pb-1">
                      <div className="text-[11px] font-semibold text-accent-700">{step.date}</div>
                      <div className="text-[13px] font-semibold text-foreground-800">{step.title}</div>
                      <p className="mt-0.5 text-xs leading-relaxed text-foreground-600">{step.text}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            {/* result */}
            <div className="rounded-xl border border-secondary-200 bg-secondary-50 p-4">
              <div className="flex items-center gap-2 text-sm font-bold text-secondary-900">
                <i className="ri-checkbox-circle-line" />
                当前状态
              </div>
              <p className="mt-2 text-[13px] leading-relaxed text-foreground-700">{event.result}</p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {event.tags.map((t) => (
                  <span
                    key={t}
                    className="whitespace-nowrap rounded-full bg-background-100 px-2.5 py-1 text-[11px] font-medium text-foreground-600"
                  >
                    #{t}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </article>
  );
}